import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { CustomFormComponent } from '@bo-client/activation-management/custom-form.component';
import { CreateGridRegistrationFormsService } from '@bo-client/activation-management/grid-registration/create-grid-registration-forms.service';
import { CreateFormsGrPlaceinserviceService } from '@bo-client/activation-management/grid-registration/forms/create-forms-gr-placeinservice.service';
import { p14aApplies } from '@bo-client/activation-management/grid-registration/grid-registration.utils';
import {
  ConsumerFormV2,
  ConsumerModel
} from '@bo-client/activation-management/grid-registration/model/consumer.model';
import {
  FeederConnectionStandard,
  FeedingPlantTypeEnum,
  NaProtectionTypeEnum,
  SymmetryConditionsEnum
} from '@bo-client/activation-management/grid-registration/model/grid-registration.enum';
import {
  GridRegistrationModel,
  PlaceInServiceModel,
  PlaceInServiceRequestForm
} from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import {
  ProducerForm,
  ProducerModel
} from '@bo-client/activation-management/grid-registration/model/producer.model';
import { PVModuleForm } from '@bo-client/activation-management/grid-registration/model/pv-module.model';
import {
  StorageForm,
  StorageModel
} from '@bo-client/activation-management/grid-registration/model/storage.model';
import { ErrorFormCommissioningEnum } from '@bo-client/activation-management/shared/forms/create-forms-errors-model';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import { DataForCustomFormModel } from '@bo-client/activation-management/shared/models/actionWithMoreInfo.model';
import { MetaService } from '@bo-client/core/api/meta-service';
import { ShowErrorModel } from '@bo-client/shared/form-errors/errors-form-model';
import { ErrorsFormsService } from '@bo-client/shared/form-errors/errors-forms.service';
import { TypedForm } from '@bo-client/shared/forms/generic-forms/typedForm.type';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { Observable, of } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';
import { UploadForm } from '@bo-client/shared/models/documentDto';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/** Place-in-service request form for Storage grid registrations (slim vs feeder). */
@Component({
  selector: 'app-gr-place-in-service-storage-request-form',
  templateUrl: './gr-place-in-service-storage-request-form.component.html',
  styleUrls: ['./gr-place-in-service-storage-request-form.component.scss']
})
export class GrPlaceInServiceStorageRequestFormComponent
  implements CustomFormComponent<GridRegistrationModel, GridRegistrationModel>, OnInit
{
  private destroyRef = inject(DestroyRef);
  createFormsGrPlaceinServiceConfirmationService = inject(CreateFormsGrPlaceinserviceService);
  private createFormsGridRegistrationService = inject(CreateGridRegistrationFormsService);
  private createFormsService = inject(CreateFormsService);
  dataShareService = inject(DataShareService);
  metaService = inject(MetaService);
  private fb = inject(FormBuilder);
  private errorsFormsService = inject(ErrorsFormsService);

  @Input() data: DataForCustomFormModel<GridRegistrationModel>;
  @Output() onConfirm: EventEmitter<GridRegistrationModel> = new EventEmitter();

  formGroup: PlaceInServiceRequestForm;
  naProtectionTypeEnum = NaProtectionTypeEnum;
  symmetryConditionsEnum = SymmetryConditionsEnum;
  totalActivePower = 0;
  totalApparentPower = 0;
  totalModulePower = 0;
  errorFormList: ShowErrorModel[] = [];
  errorFieldRequired = 'Feld ist erforderlich';
  errorOptionRequired = 'Bitte wählen Sie eine Option aus';
  errorCheckRequired = 'Bitte bestätigen Sie die Bedingung';
  model: GridRegistrationModel;

  showExternalIdField$ = of(false);
  showExternalCustomerIdField$ = of(false);

  hasProduction$: Observable<boolean>;

  formValue$: Observable<PlaceInServiceRequestForm['value']>;

  get placeInForm() {
    return this.formGroup.get('placeInService') as FormGroup;
  }

  get producers() {
    return this.formGroup.controls['producers'] as FormArray;
  }

  get pvModules() {
    return this.formGroup.controls['pvModules'] as FormArray;
  }

  get storages() {
    return this.formGroup.controls['storages'] as FormArray;
  }
  // Wird verwendet, da momentan nur ein Speicher an einer Netzanmeldung hängen kann
  get firstStorage() {
    return this.storages.at(0) as StorageForm;
  }

  get consumers() {
    return this.formGroup.controls['consumers'] as FormArray;
  }
  // Speicher als Verbraucher
  get firstConsumer() {
    return this.consumers.at(0) as TypedForm<ConsumerModel>;
  }

  get firstProducer() {
    return this.producers.at(0) as ProducerForm | undefined;
  }

  get uploads(): FormArray {
    return this.formGroup.get('uploads') as FormArray;
  }

  get componentsCardHasError(): boolean {
    if (this.storages?.status === 'INVALID' || this.consumers?.status === 'INVALID') {
      return true;
    }
    return (
      StorageModel.hasProduction(this.firstStorage?.value?.storageOperationType) &&
      this.producers?.status === 'INVALID'
    );
  }

  get meterOne() {
    return this.formGroup.get('meterOne');
  }

  get meterTwo() {
    return this.formGroup.get('meterTwo');
  }

  get meterThree() {
    return this.formGroup.get('meterThree');
  }

  get meteringConcept() {
    return this.formGroup.get('meteringConcept.id') as FormControl;
  }

  get feederConnectionStandard() {
    return this.model.feederConnectionStandard;
  }

  get initialP14aApplies() {
    const firstConsumer = this.model?.consumers[0];
    if (!firstConsumer) {
      return false;
    }
    return p14aApplies('Storage', consumerForP14aCheck(firstConsumer, this.model?.storages[0]?.count));
  }

  ngOnInit(): void {
    this.model = this.data.model;

    this.formGroup = this.fb.group(
      {
        deviceType: [null as number],
        placeInService: this.createFormsGrPlaceinServiceConfirmationService.createPlaceInServiceRequestForm(
          new PlaceInServiceModel(),
          false
        ),
        newProduction: [null as number],
        newInstalledPower: [null as number],
        // Detaillierte Aufschlüsselung von Modulen / Wechselrichtern
        pvModules: this.fb.array<PVModuleForm>([]),
        producers: this.fb.array<ProducerForm>([]),
        storages: this.fb.array<StorageForm>([]),
        consumers: this.fb.array<ConsumerFormV2>([]),
        uploads: this.fb.array<UploadForm>([]),
        meterNumber: ['', Validators.required],
        meterOne: this.fb.group({ meterNo: [''], value: [''], obisCode: [''] }),
        meterTwo: this.fb.group({ meterNo: [''], value: [''], obisCode: [''] }),
        meterThree: this.fb.group({ meterNo: [''], value: [''], obisCode: [''] }),
        meteringConcept: this.fb.group({ id: [null as number, Validators.required] }),
        maStRNumberProducer: [''],
        maStRSSENumberStorage: [''],
        maStRNumberStorage: [''],
        maStREEGNumber: [''],
        maStRNumber: [''],
        maStRAcceptedDeclaration: [false],
        maStRRegistrationDate: [null as Date],
        customer: this.createFormsService.createContactDataFormV2({}, false)
      },
      {
        // No PV module power threshold validator for storage-only place-in flow.
        updateOn: 'change'
      }
    );

    /*this.showFeedInManagement$ = this.dataShareService
      .getSingleTenantConfig$('gridRegistration.showFeedInManagementForPlaceInServiceRequest')
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        switchMap((config) => {
          if (!config) return of(false);
          return this.formGroup.controls.pvModules.valueChanges.pipe(
            startWith(this.formGroup.controls.pvModules.value),
            map((pvModules) => {
              const installedPower = pvModules
                .map((item) => item.count * item.modulePowerWatt)
                .reduce((acc, cur) => acc + cur, 0);
              return installedPower / 1000 > 25;
            })
          );
        }),
        distinctUntilChanged(),
        tap((value) => {
          const feedInManagementSerialNumberControl = this.formGroup.controls.feedInManagementSerialNumber;
          if (!value) {
            feedInManagementSerialNumberControl.setValidators(null);
          } else {
            feedInManagementSerialNumberControl.setValidators([Validators.required]);
          }
          feedInManagementSerialNumberControl.updateValueAndValidity();
        }),
        shareReplay(1)
      );*/
    this.formValue$ = this.formGroup.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(this.formGroup.value),
      shareReplay(1)
    );

    this.hasProduction$ = this.formValue$.pipe(
      map((formValue) => StorageModel.hasProduction(formValue.storages[0]?.storageOperationType))
    );

    this.initShowExternalIdFields();
    if (this.model.producers && this.model.producers.length > 0 && !this.model.newProduction) {
      this.model.producers.forEach((x) => {
        this.totalActivePower = this.totalActivePower + x.acActivePowerMax * x.count;
        this.totalApparentPower = this.totalApparentPower + x.acApparentPowerMax * x.count;
      });

      /**
       * Handle Backwards compatibility
       * it is possible that we have old processes that don't have
       *   newProduction
       *   newInstalledPower
       *
       * in that case we want to patch it with the accumuilated valiue of the installed components
       */
      this.model.newProduction = this.totalApparentPower;
    }
    if (this.model.pvModules && this.model.pvModules.length > 0 && !this.model.newInstalledPower) {
      this.model.pvModules.forEach((x) => {
        this.totalModulePower = this.totalModulePower + (x.modulePowerWatt * x.count) / 1000;
      });

      /**
       * Handle Backwards compatibility
       * it is possible that we have old processes that don't have
       *   newProduction
       *   newInstalledPower
       *
       * in that case we want to patch it with the accumuilated valiue of the installed components
       */
      this.model.newInstalledPower = this.totalModulePower;

      //  this.setFieldValidator('effectiveModulePowerWatt', 'required', true, this.totalModulePower);
    }

    this.initArrayForms();

    // nochmal alles reinbügeln
    this.formGroup.patchValue(this.model, { emitEvent: true });

    this.configurePlaceInServiceConsumerValidators();
    this.syncProducersWithStorageOperationType();
    this.markComponentFormsAsTouched();
    this.firstStorage?.controls.storageOperationType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.syncProducersWithStorageOperationType();
        this.markComponentFormsAsTouched();
      });

    this.dataShareService.uiAdaptions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((uiAdaptions) => {
      const invoiceMeteringCheckDoneOptional =
        uiAdaptions.find(
          (adaption) =>
            adaption.anchor === 'gridRegistration.placeInServiceRequest.invoiceMeteringCheckDoneOptional'
        )?.value || 'false';
      const powerDirectionSensorCheckDone =
        uiAdaptions.find(
          (adaption) =>
            adaption.anchor === 'gridRegistration.placeInServiceRequest.powerDirectionSensorCheckDoneOptional'
        )?.value || 'false';

      if (!this.model.feederConnectionStandard || this.model.feederConnectionStandard === 'VDE4105') {
        this.setFieldValidator('effectiveAndRequestedValuesMatch', 'mustTrue');
        if (invoiceMeteringCheckDoneOptional === 'true' || invoiceMeteringCheckDoneOptional === 'show') {
          this.setFieldValidator('invoiceMeteringCheckDone', 'optional');
        } else {
          this.setFieldValidator('invoiceMeteringCheckDone', 'mustTrue');
        }
        this.setFieldValidator('vdeCertificateExists', 'mustTrue');
        if (powerDirectionSensorCheckDone === 'true' || powerDirectionSensorCheckDone === 'show') {
          this.setFieldValidator('powerDirectionSensorCheckDone', 'optional');
        } else {
          this.setFieldValidator('powerDirectionSensorCheckDone', 'mustTrue');
        }
        this.setFieldValidator('vdeArN4100_55_CertificateExists', 'required');
        this.setFieldValidator('powerProtectionCertificateExists', 'mustTrue');
        this.setFieldValidator('factorCosphiCheck', 'mustTrue');
        this.setFieldValidator('naProtection', 'required');
        this.setFieldValidator('overvoltageProtectionType', 'required');
        this.setFieldValidator('pMonitoringExists', 'required');
        this.setFieldValidator('eegConfirmation', 'optional');
        this.setFieldValidator('flawlessFunctionGuarantee', 'optional');
        this.setFieldValidator('symmetryConditions', 'required');
        this.setFieldValidator('placeInServiceDatum', 'required');
        this.setFieldValidator('vdeConditions', 'mustTrue');
      }
    });
  }

  setValidatorsNaProtection() {
    if (this.placeInForm.get('naProtection')) {
      if (this.placeInForm.get('naProtection').value == NaProtectionTypeEnum.Integrated) {
        this.setFieldValidator('overvoltageReleaseTestDone', 'optional');
        this.setFieldValidator('triggerCircuitTestDone', 'optional');
      }
      if (this.placeInForm.get('naProtection').value == NaProtectionTypeEnum.Centralised) {
        this.setFieldValidator('overvoltageReleaseTestDone', 'mustTrue');
        this.setFieldValidator('triggerCircuitTestDone', 'mustTrue');
      }
      this.placeInForm.get('overvoltageReleaseTestDone').patchValue(null);
      this.placeInForm.get('triggerCircuitTestDone').patchValue(null);
      this.placeInForm.get('overvoltageReleaseTestDone').markAsTouched();
      this.placeInForm.get('triggerCircuitTestDone').markAsTouched();
    }
  }

  setValidatorsMonitoring() {
    if (this.placeInForm.get('pMonitoringExists')) {
      if (this.placeInForm.get('pMonitoringExists').value == false) {
        this.setFieldValidator('paveTestDone', 'optional');
        this.setFieldValidator('effectivePave', 'optional');
      }
      if (this.placeInForm.get('pMonitoringExists').value == true) {
        this.setFieldValidator('paveTestDone', 'mustTrue');
        this.setFieldValidator('effectivePave', 'required');
      }
      this.placeInForm.get('paveTestDone').patchValue(null);
      this.placeInForm.get('effectivePave').patchValue(null);
      this.placeInForm.get('paveTestDone').markAsTouched();
      this.placeInForm.get('effectivePave').markAsTouched();
    }
  }

  setSymmetryConditionsMonitoring() {
    if (this.placeInForm.get('symmetryConditions')) {
      if (
        this.placeInForm.get('symmetryConditions').value !=
        this.symmetryConditionsEnum.SinglePhaseDistribution
      ) {
        this.setFieldValidator('addedGenerationL1', 'optional');
        this.setFieldValidator('addedGenerationL2', 'optional');
        this.setFieldValidator('addedGenerationL3', 'optional');
      }
      if (
        this.placeInForm.get('symmetryConditions').value ==
        this.symmetryConditionsEnum.SinglePhaseDistribution
      ) {
        this.setFieldValidator('addedGenerationL1', 'required');
        this.setFieldValidator('addedGenerationL2', 'required');
        this.setFieldValidator('addedGenerationL3', 'required');
      }
      this.placeInForm.get('addedGenerationL1').patchValue(null);
      this.placeInForm.get('addedGenerationL2').patchValue(null);
      this.placeInForm.get('addedGenerationL3').patchValue(null);
      this.placeInForm.get('addedGenerationL1').markAsTouched();
      this.placeInForm.get('addedGenerationL2').markAsTouched();
      this.placeInForm.get('addedGenerationL3').markAsTouched();
    }
  }

  cancel() {
    this.onConfirm.emit(null);
  }

  confirm() {
    this.formGroup.markAllAsTouched();
    if (this.isValid()) {
      const result = {
        ...this.model,
        ...(this.formGroup.getRawValue() as unknown as Partial<GridRegistrationModel>)
      };
      this.onConfirm.emit(result);
    }
  }

  setFieldValidator(
    fieldName: string,
    action: 'required' | 'optional' | 'mustTrue',
    valueLessOrEqual?: boolean,
    valueToCompare?: number
  ) {
    const selectedControl = this.placeInForm.get(fieldName);
    switch (action) {
      case 'required':
        selectedControl.setValidators(
          !valueLessOrEqual
            ? [Validators.required]
            : [Validators.required, this.lessOrEqual(fieldName, valueToCompare)]
        );
        break;
      case 'optional':
        selectedControl.clearValidators();
        break;
      case 'mustTrue':
        selectedControl.setValidators([Validators.required, this.createFormsService.mustTrue]);
        break;
    }
  }

  lessOrEqual(fieldName: string, valueToCompare: number): ValidatorFn {
    return (control: FormGroup): ValidationErrors | null => {
      if (control.errors && !control.errors.lessOrEqual) {
        return;
      }
      if (control.value > valueToCompare) {
        return { lessOrEqual: true };
      } else {
        return null;
      }
    };
  }

  isValid(): boolean {
    this.loadErrorsList();
    return this.formGroup.valid;
  }

  loadErrorsList() {
    this.errorFormList = this.errorsFormsService.checkTwoLevelFormErrors(
      this.formGroup,
      ErrorFormCommissioningEnum,
      null
    );
  }

  private initArrayForms() {
    // PV-Module
    this.pvModules.clear();
    this.model.pvModules.forEach((pvModule) => {
      const pvModuleGroup = this.createFormsGridRegistrationService.createPVModuleFormSimple(pvModule);
      this.pvModules.push(pvModuleGroup);
    });
    // Erzeuger
    this.producers.clear();
    this.model.producers.forEach((producer) => {
      const producerGroup = this.createFormsGridRegistrationService.createProducerFormSimple(
        producer,
        this.model
      );
      this.producers.push(producerGroup);
    });
    // Speicher
    this.storages.clear();
    if (this.model.storages.length > 0) {
      this.model.storages.forEach((storage) => {
        const storageGroup = this.createFormsGridRegistrationService.createStorageFormV2(
          storage,
          this.model,
          false
        );
        this.storages.push(storageGroup);
      });
    }

    //Verbraucher
    this.consumers.clear();
    if (this.model.consumers.length > 0) {
      this.model.consumers.forEach((consumer, index) => {
        const consumerGroup = this.createFormsGridRegistrationService.createConsumerFormV2(consumer);
        this.consumers.push(consumerGroup);
        if (index === 0) {
          consumerGroup.setValidators(
            noP14aChange(this.initialP14aApplies, () => this.storages.at(0)?.value?.count)
          );
        }
      });
    }

    // Uploads
    this.uploads.clear();
    this.model.uploads.forEach((upload) => {
      const uploadFormGroup = this.createFormsService.createUploadForm(upload);
      this.uploads.push(uploadFormGroup);
    });
  }

  /** Only validate consumer fields that appear in storage-formpart-v2 for place-in-service. */
  private configurePlaceInServiceConsumerValidators(): void {
    const consumer = this.firstConsumer;
    if (!consumer) {
      return;
    }
    for (const field of ['manufacturer', 'count'] as const) {
      consumer.controls[field].clearValidators();
      consumer.controls[field].updateValueAndValidity({ emitEvent: false });
    }
  }

  private syncProducersWithStorageOperationType(): void {
    const hasProduction = StorageModel.hasProduction(this.firstStorage?.value?.storageOperationType);
    if (!hasProduction) {
      this.producers.clear();
      return;
    }
    if (this.producers.length === 0) {
      this.producers.push(
        this.createFormsGridRegistrationService.createProducerFormSimple(new ProducerModel(), this.model)
      );
    }
  }

  /** Align field-level errors with the components card, which flags INVALID without a touched check. */
  private markComponentFormsAsTouched(): void {
    this.storages.markAllAsTouched();
    this.consumers.markAllAsTouched();
    if (StorageModel.hasProduction(this.firstStorage?.value?.storageOperationType)) {
      this.producers.markAllAsTouched();
    }
  }

  private initShowExternalIdFields() {
    const config$ = this.dataShareService.tenantConfig$.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((config) => config?.gridRegistration),
      shareReplay(1)
    );
    this.showExternalIdField$ = config$.pipe(map((config) => !!config?.showExternalIdField));
    this.showExternalCustomerIdField$ = config$.pipe(map((config) => !!config?.showExternalCustomerIdField));
  }

  readonly FeederConnectionStandard = FeederConnectionStandard;
  readonly of = of;
  readonly FeedingPlantTypeEnum = FeedingPlantTypeEnum;

  get documentsUploadInfoUIAdaptionName() {
    return (
      'gridRegistration.' +
      this.model.gridRegistrationType.valueOf() +
      '.placeInServiceRequest.documentsUploadInfo'
    );
  }
}

function consumerForP14aCheck(consumer: ConsumerModel, storageCount?: number): ConsumerModel {
  return {
    ...consumer,
    count: consumer.count ?? storageCount ?? 0
  };
}

function noP14aChange(initialP14aApplies: boolean, storageCount: () => number | undefined): ValidatorFn {
  return (storageAsConsumerControl: AbstractControl<ConsumerModel>): ValidationErrors | null => {
    const currentP14aApplies = p14aApplies(
      'Storage',
      consumerForP14aCheck(storageAsConsumerControl.value, storageCount())
    );
    if (initialP14aApplies !== currentP14aApplies) {
      return currentP14aApplies ? { powerExceeded: true } : { powerDeceeded: true };
    } else return null;
  };
}
